/**
 * WorkstationUpdatesPanel — Windows Update del equipo, dentro de la ficha.
 *
 * Los datos ya los recoge el agente (`collectors/self_windows.collect_windows_updates`)
 * y viajan en el blob de inventario como `windows_updates`: aquí solo se
 * ordenan y se pintan. No hay consulta extra ni agente nuevo.
 *
 * Lo que todavía NO trae el colector se dice en la propia pantalla en vez de
 * inventarlo: tamaño de la descarga, recuento de fallidas e historial completo
 * de 30 días necesitan una búsqueda `QueryHistory` que aún no se hace.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import HelpDot from '../academy/HelpDot';
import Dialog from './common/Dialog';
import api from '../services/api';

const SEVERITY_META = {
  critical: { label: 'Crítica', pill: 'sf2-pill--bad' },
  important: { label: 'Importante', pill: 'sf2-pill--wa' },
  moderate: { label: 'Moderada', pill: '' },
  low: { label: 'Baja', pill: '' },
  unspecified: { label: 'Sin clasificar', pill: '' },
};

/** Tipo deducido del título: la API no expone la categoría en el blob. */
function kindOf(item) {
  const titulo = String(item?.title || '').toLowerCase();
  if (titulo.includes('defender') || titulo.includes('inteligencia') || titulo.includes('definitions')) {
    return 'Definiciones';
  }
  if (titulo.includes('driver') || titulo.includes('controlador')) return 'Controlador';
  if (titulo.includes('.net') || titulo.includes('framework')) return 'Calidad';
  const sev = String(item?.severity || '').toLowerCase();
  if (sev === 'critical' || sev === 'important') return 'Seguridad';
  return 'Actualización';
}

function formatDate(value) {
  if (!value) return '—';
  const parsed = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('es-ES');
}

export default function WorkstationUpdatesPanel({
  updates,
  companyId,
  agentId,
  ciType = 'workstation',
  ciId,
}) {
  const data = updates && typeof updates === 'object' ? updates : null;
  const [selected, setSelected] = useState([]);
  const [confirmar, setConfirmar] = useState(null); // null | 'selected' | 'all'
  const [busy, setBusy] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  // Comando de instalación en curso: se sigue con polling para no dejar al
  // usuario a ciegas. Antes se encolaba y no se sabía nada más.
  const [job, setJob] = useState(null);

  const consultarJob = useCallback(async (commandId) => {
    if (!companyId || !commandId) return null;
    try {
      const res = await api.getAgentCommands(companyId, { agent_id: agentId });
      const rows = res?.commands || res?.items || res?.data || [];
      return rows.find((row) => String(row.id) === String(commandId)) || null;
    } catch {
      return null;
    }
  }, [companyId, agentId]);

  useEffect(() => {
    if (!job?.id || ['done', 'error', 'cancelled'].includes(job.status)) return undefined;
    let vivo = true;
    const tick = async () => {
      const row = await consultarJob(job.id);
      if (!vivo || !row) return;
      setJob((prev) => ({ ...prev, status: row.status, result: row.result }));
    };
    tick();
    const timer = setInterval(tick, 6000);
    return () => { vivo = false; clearInterval(timer); };
  }, [job?.id, job?.status, consultarJob]);

  const pendientes = Array.isArray(data?.pending) ? data.pending : [];
  const recientes = Array.isArray(data?.recent) ? data.recent : [];

  // Lo instalado en los últimos 30 días, según los últimos registros que sí
  // manda el agente (`Win32_QuickFixEngineering`, 5 entradas).
  const instaladas30d = useMemo(() => {
    const limite = Date.now() - 30 * 24 * 3600 * 1000;
    return recientes.filter((item) => {
      const fecha = new Date(item.installed_on || '');
      return !Number.isNaN(fecha.getTime()) && fecha.getTime() >= limite;
    }).length;
  }, [recientes]);

  if (!data) {
    return (
      <div className="dv-section">
        <h3 className="dv-section-title" style={{ textAlign: 'left' }}>
          Actualizaciones de Windows
          <HelpDot topic="listados.workstation_updates" label="Ayuda: actualizaciones del equipo" />
        </h3>
        <p className="sf2-hint" style={{ margin: 0 }}>
          El agente todavía no ha reportado Windows Update en este equipo. Un
          «Rescan completo», en la pestaña Agente, lo vuelve a pedir.
        </p>
      </div>
    );
  }

  if (data.available === false || data.search_ok === false) {
    return (
      <div className="dv-section">
        <h3 className="dv-section-title" style={{ textAlign: 'left' }}>
          Actualizaciones de Windows
          <HelpDot topic="listados.workstation_updates" label="Ayuda: actualizaciones del equipo" />
        </h3>
        <div className="sf2-card">
          <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ExclamationTriangleIcon style={{ width: 18, height: 18, color: 'var(--amber-600, #d97706)' }} />
            Windows Update no se pudo consultar en este equipo.
          </p>
          {data.error && (
            <p className="sf2-hint" style={{ margin: 0 }}>
              {data.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const toggle = (idx) => {
    setSelected((prev) => (
      prev.includes(idx) ? prev.filter((n) => n !== idx) : [...prev, idx]
    ));
  };

  const todosMarcados = pendientes.length > 0 && selected.length === pendientes.length;

  /** Kbs a instalar segun el modo elegido en el dialogo. */
  const kbsPara = (modo) => {
    if (modo === 'all') {
      return pendientes.map((item) => item.kb).filter(Boolean);
    }
    return selected.map((idx) => pendientes[idx]?.kb).filter(Boolean);
  };

  const lanzarInstalacion = async () => {
    const modo = confirmar;
    const kbs = kbsPara(modo);
    setConfirmar(null);
    setBusy(true);
    setError(null);
    setAviso(null);
    try {
      const res = await api.enqueueAgentCommand(companyId, {
        agent_id: agentId,
        command: 'windows_update_install',
        ci_type: ciType,
        ci_id: ciId,
        payload: { kbs },
      });
      const commandId = res?.command?.id || null;
      setJob({ id: commandId, status: 'pending', kbs });
      setAviso(
        kbs.length
          ? `Instalación encolada para ${kbs.length} actualización(es). Puede tardar bastante.`
          : 'Instalación de todas las pendientes encolada. Puede tardar bastante.',
      );
      setSelected([]);
    } catch (e) {
      setError(e?.message || 'No se pudo encolar la instalación');
    } finally {
      setBusy(false);
    }
  };

  /** Software que NO viene de Windows Update: Chocolatey hace `choco upgrade all`. */
  const lanzarUpgradeChoco = async () => {
    setBusy(true);
    setError(null);
    setAviso(null);
    try {
      const res = await api.upgradeAllSoftware(
        { targets: [{ ci_type: ciType, ci_id: ciId }] },
        companyId,
      );
      setAviso(
        `Actualización de software de terceros encolada (${res?.count ?? 1} equipo). `
        + 'Chocolatey actualiza los paquetes instalados por él; lo demás hay que reinstalarlo.',
      );
    } catch (e) {
      setError(e?.message || 'No se pudo encolar la actualización de paquetes');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dv-tab-pane">
      <div className="sf2-hl" style={{ marginBottom: 12 }}>
        <div className="sf2-kpi">
          <span>Pendientes</span>
          <b style={{ color: pendientes.length ? 'var(--amber-600, #d97706)' : undefined }}>
            {pendientes.length}
          </b>
        </div>
        <div className="sf2-kpi">
          <span>Instaladas (30 días)</span>
          <b>{data.installed_30d ?? instaladas30d}</b>
        </div>
        <div className="sf2-kpi">
          <span>Fallidas</span>
          <b
            style={{ color: data.failed_30d ? 'var(--red-600, #dc2626)' : undefined }}
            title={
              data.failed_30d == null
                ? 'Este agente aún no lee el historial de errores de Windows Update'
                : 'Intentos fallidos en los últimos 30 días'
            }
          >
            {data.failed_30d ?? '—'}
          </b>
        </div>
        <div className="sf2-kpi">
          <span>Última comprobación</span>
          <b style={{ fontSize: '0.95rem' }}>{formatDate(data.collected_at)}</b>
        </div>
      </div>

      <div className="sf2-card" style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <InformationCircleIcon style={{ width: 18, height: 18, flexShrink: 0, marginTop: 2 }} />
        <p style={{ margin: 0 }}>
          Instalar desde aquí lanza el comando en el equipo: el agente descarga e
          instala en segundo plano y sigue latiendo mientras lo hace. El
          resultado aparece también en la pestaña <strong>Agente</strong>.
        </p>
      </div>

      {job && (
        <div className="sf2-card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {['done', 'error', 'cancelled'].includes(job.status) ? (
              job.status === 'done' ? (
                <CheckCircleIcon style={{ width: 18, height: 18, color: 'var(--green-600, #16a34a)' }} />
              ) : (
                <XCircleIcon style={{ width: 18, height: 18, color: 'var(--red-600, #dc2626)' }} />
              )
            ) : (
              <ArrowPathIcon
                className="sf2-spin"
                style={{ width: 18, height: 18, color: 'var(--amber-600, #d97706)' }}
              />
            )}
            <strong>
              {job.status === 'pending' && 'Instalación en cola…'}
              {job.status === 'running' && 'Instalando en el equipo…'}
              {job.status === 'received' && 'Instalación recibida por el agente…'}
              {job.status === 'done' && 'Instalación terminada'}
              {job.status === 'error' && 'La instalación falló'}
              {job.status === 'cancelled' && 'Instalación cancelada'}
            </strong>
            {job.id && <span className="sf2-hint" style={{ margin: 0 }}>comando #{job.id}</span>}
          </p>
          {job.status === 'done' && job.result && (
            <p style={{ margin: '6px 0 0' }}>
              {job.result.installed?.length || 0} instalada(s)
              {job.result.failed?.length ? ` · ${job.result.failed.length} fallida(s)` : ''}
              {job.result.reboot_required ? ' · requiere reinicio' : ''}
            </p>
          )}
          {job.status === 'error' && job.result && (
            <p style={{ margin: '6px 0 0' }}>{job.result.error || job.result.reason || 'Sin detalle'}</p>
          )}
          {!['done', 'error', 'cancelled'].includes(job.status) && (
            <p className="sf2-hint" style={{ margin: '6px 0 0' }}>
              El equipo sigue trabajando; esta tarjeta se actualiza sola.
            </p>
          )}
        </div>
      )}

      {aviso && (
        <div className="sf2-card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleIcon style={{ width: 18, height: 18, color: 'var(--green-600, #16a34a)' }} />
            {aviso}
          </p>
        </div>
      )}
      {error && (
        <div className="sf2-card" style={{ marginBottom: 12 }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <XCircleIcon style={{ width: 18, height: 18, color: 'var(--red-600, #dc2626)' }} />
            {error}
          </p>
        </div>
      )}

      <div className="sf2-card" style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            marginBottom: 10,
            justifyContent: 'flex-start',
          }}
        >
          <h3 style={{ margin: 0 }}>Actualizaciones disponibles</h3>
          <HelpDot topic="listados.workstation_updates" label="Ayuda: actualizaciones del equipo" />
          {data.reboot_required && (
            <span className="sf2-pill sf2-pill--wa">
              <ExclamationTriangleIcon style={{ width: 13, height: 13, marginRight: 4 }} />
              Reinicio pendiente
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={() => setConfirmar('selected')}
              disabled={busy || selected.length === 0 || !agentId}
              title={selected.length ? 'Instalar las marcadas' : 'Marca alguna actualización'}
            >
              Instalar seleccionadas{selected.length ? ` (${selected.length})` : ''}
            </button>
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              onClick={() => setConfirmar('all')}
              disabled={busy || pendientes.length === 0 || !agentId}
              title="Instalar todas las pendientes"
            >
              Instalar todas
            </button>
          </div>
        </div>

        {pendientes.length === 0 ? (
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircleIcon style={{ width: 18, height: 18, color: 'var(--green-600, #16a34a)' }} />
            No hay actualizaciones pendientes en este equipo.
          </p>
        ) : (
          <div className="sf2-table-wrap">
            <table className="sf2-table">
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todas"
                      checked={todosMarcados}
                      onChange={() => setSelected(
                        todosMarcados ? [] : pendientes.map((_, idx) => idx),
                      )}
                    />
                  </th>
                  <th>Actualización</th>
                  <th>KB</th>
                  <th>Tipo</th>
                  <th>Publicada</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map((item, idx) => {
                  const sev = SEVERITY_META[String(item.severity || '').toLowerCase()] || null;
                  return (
                    <tr key={`${item.kb || item.title}-${idx}`}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${item.title}`}
                          checked={selected.includes(idx)}
                          onChange={() => toggle(idx)}
                        />
                      </td>
                      <td>{item.title}</td>
                      <td>{item.kb ? <code>{item.kb}</code> : <span className="sf2-hint">—</span>}</td>
                      <td>{kindOf(item)}</td>
                      <td>{formatDate(item.published)}</td>
                      <td>
                        <span className={`sf2-pill ${sev?.pill || 'sf2-pill--wa'}`}>
                          {item.downloaded ? 'Descargada' : 'Pendiente'}
                        </span>
                        {item.reboot_required && (
                          <span className="sf2-pill sf2-pill--wa" style={{ marginLeft: 4 }}>
                            Reinicio
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="sf2-card">
        <h3 style={{ marginTop: 0, textAlign: 'left' }}>Historial reciente</h3>
        {recientes.length === 0 ? (
          <p className="sf2-hint" style={{ margin: 0 }}>
            El agente no ha reportado actualizaciones instaladas recientemente.
          </p>
        ) : (
          <div className="sf2-table-wrap">
            <table className="sf2-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Actualización</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {recientes.map((item) => (
                  <tr key={item.hotfix_id}>
                    <td>{formatDate(item.installed_on)}</td>
                    <td>
                      <code>{item.hotfix_id}</code>
                      {item.description ? ` · ${item.description}` : ''}
                    </td>
                    <td>
                      <span className="sf2-pill sf2-pill--ok">
                        <CheckCircleIcon style={{ width: 13, height: 13, marginRight: 4 }} />
                        Correcta
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="sf2-hint" style={{ margin: '8px 0 0' }}>
          <ClockIcon style={{ width: 13, height: 13, marginRight: 4 }} />
          Son los últimos registros del equipo. El historial completo de 30 días,
          con los intentos fallidos, necesita una consulta que el agente aún no hace.
        </p>
      </div>

      <div className="sf2-card" style={{ marginTop: 12 }}>
        <h3 style={{ marginTop: 0, textAlign: 'left' }}>
          Software de terceros (Chocolatey)
          <HelpDot topic="agente.paquetes" anchor="agente.paquetes" />
        </h3>
        <p className="sf2-hint" style={{ marginTop: 0 }}>
          Windows Update sólo cubre el sistema. Chrome, Vivaldi, Acrobat, 7-Zip y compañía
          se actualizan con <code>choco upgrade all</code>: actualiza de golpe todos los paquetes
          que Chocolatey tenga instalados en este equipo. Lo que no se instaló con Chocolatey
          no lo toca — para eso está el catálogo de paquetes de la empresa, en la pestaña{' '}
          <strong>Software</strong>.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            className="dv-btn dv-btn-primary"
            onClick={lanzarUpgradeChoco}
            disabled={busy || !agentId}
          >
            <ArrowPathIcon style={{ width: 16, height: 16, marginRight: 6 }} />
            Actualizar paquetes del equipo
          </button>
          <span className="sf2-hint" style={{ margin: 0 }}>
            Tarda varios minutos. El resultado se ve en la pestaña <strong>Software</strong>.
          </span>
        </div>
        {!agentId && (
          <p className="sf2-hint" style={{ margin: '8px 0 0' }}>
            <InformationCircleIcon style={{ width: 13, height: 13, marginRight: 4 }} />
            Sin agente no hay a quién pedirle la actualización.
          </p>
        )}
      </div>

      {data.service?.status && data.service.status.toLowerCase() !== 'running' && (
        <div className="sf2-card" style={{ marginTop: 12 }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <XCircleIcon style={{ width: 18, height: 18, color: 'var(--red-600, #dc2626)' }} />
            El servicio <code>wuauserv</code> está <strong>{data.service.status}</strong>: mientras
            siga así, este equipo no recibirá actualizaciones.
          </p>
        </div>
      )}

      {confirmar && (
        <Dialog
          title="Instalar actualizaciones"
          icon={<ArrowPathIcon style={{ width: 20, height: 20 }} />}
          onClose={() => !busy && setConfirmar(null)}
          busy={busy}
          footer={(
            <>
              <button
                type="button"
                className="dv-btn dv-btn-secondary"
                onClick={() => setConfirmar(null)}
                disabled={busy}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="dv-btn dv-btn-primary"
                onClick={lanzarInstalacion}
                disabled={busy}
              >
                {busy ? 'Encolando…' : 'Instalar'}
              </button>
            </>
          )}
        >
          <p style={{ margin: '0 0 10px' }}>
            {confirmar === 'all'
              ? `Se instalarán las ${pendientes.length} actualizaciones pendientes.`
              : `Se instalarán ${selected.length} actualización(es) de las marcadas.`}
          </p>
          <p style={{ margin: 0 }}>
            El equipo descargará e instalará en segundo plano y puede tardar
            bastante. Si alguna pide reinicio, <strong>no se reinicia solo</strong>:
            el aviso queda en la ficha.
          </p>
        </Dialog>
      )}
    </div>
  );
}
